import "./DocumentManagement.scss";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  Input,
  InputAdornment,
  Link,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Breadcrumb } from "../../breadcrumb/components/Breadcrumb";
import {
  DOCUMENT_MANAGEMENT_SHOW_USERS_TAB,
  isDocumentCategorySelected,
} from "../constants/documentManagementConstants";
import {
  documentLibrarySortPropertyType,
  useDocumentLibraryHeaders,
} from "../constants/documentManagementConstants";
import type { TableDataActionsModel } from "../../user-management/models/UserManagement";
import { tableSorting } from "../../../shared/utils/Utils";
import type { Order } from "../../../shared/models/TableModels";
import type {
  DatasetOptionUi,
  DocumentRowUi,
  PreviewItemUi,
  RoleOptionUi,
} from "../types/documentManagementUiTypes";
import {
  findDatasetById,
  resolveUploadDatasetTarget,
} from "../utils/documentManagementUiUtils";
import {
  applyPersistedMetadataJobIds,
  clearPersistedIngestSession,
  collectMetadataJobTargets,
  hasPersistedMetadataSession,
  buildIngestSessionPayloadForPersist,
  mergeMetadataJobsForPreview,
  mergeMetadataJobsFromDocumentMap,
  metadataJobsForSessionBootstrap,
  metadataJobsKeySignature,
  previewHasActiveMetadataBatch,
  readPersistedIngestSession,
  resolveMetadataJobByDocumentId,
  writePersistedIngestSession,
} from "../utils/documentManagementIngestSession";
import { DocumentLibraryTable } from "./library/DocumentLibraryTable";
import { DocumentIngestionDone } from "./review/DocumentIngestionDone";
import {
  DocumentIngestionProgress,
  type ConfirmEnqueueFailureUi,
} from "./review/DocumentIngestionProgress";
import { DocumentMetadataPreview } from "./review/DocumentMetadataPreview";
import { DocumentUploadFailuresAlert } from "./review/DocumentUploadFailuresAlert";
import {
  DocumentManagementDialogs,
  type DeleteConfirmState,
} from "./shared/DocumentManagementDialogs";
import { DocumentUploadPanel } from "./upload/DocumentUploadPanel";
import { DocumentManagementUsersTab } from "./users/DocumentManagementUsersTab";
import { useAPI } from "../../../shared/services/api/API";
import { OPTIVAL_CONSTANTS } from "../../../shared/Constants";
import {
  buildBulkDeleteBody,
  buildConfirmIngestionBody,
  buildReconfirmIngestionBodyFromDocument,
  buildRemovedDocumentIngestionJobEvent,
  chunkArray,
  mapConfirmEnqueueFailures,
  RAG_BULK_DELETE_MAX_BATCH,
  preservePreviewSessionEdits,
  buildUploadFormData,
  mapDocumentRowToPreview,
  ingestionJobStreamUrl,
  isDocumentIngestionPipeline,
  isDocumentRolledBack,
  ingestJobsAwaitDocumentValidation,
  isDocumentProcessing,
  isMetadataStatusPending,
  metadataJobStreamUrl,
  isIngestJobActive,
  isIngestJobTerminal,
  isMetadataJobActive,
  isMetadataJobTerminal,
  isPendingReviewUpload,
  mapDocumentListItem,
  mapUploadItemToPreview,
  mergePreviewItems,
  previewItemsAfterConfirm,
  previewItemsFromPendingDocuments,
  previewItemsNeedMetadataWait,
  previewItemsUseMetadataJobStream,
  RAG_API,
  type IngestJobEventUi,
  type IngestJobUi,
  type RagBulkDeleteResponse,
  type RagConfirmIngestionResponse,
  type RagIngestionConfirmation,
  type RagDocumentListResponse,
  mapUploadFailuresDisplay,
  type RagUploadApiResponse,
  type RagUploadFailureApiItem,
  buildRoleAccessPatchBody,
} from "../services/ragDocumentApi";
import {
  extractApiErrorMessage,
  springErrorMessage,
} from "../services/apiErrorUtils";
import { normalizeRoleAccessForSave } from "../utils/documentManagementUiUtils";
import {
  filterFilesForUploadMode,
  normalizeUploadFileSelection,
  uploadBasename,
  type UploadMediaMode,
} from "../utils/uploadFileUtils";
import {
  filterLibraryRowsByMedia,
  type LibraryMediaFilter,
} from "../utils/documentLibraryMediaUtils";
import {
  formatConfirmEnqueueFailureLine,
  friendlyConfirmIngestionError,
  friendlyConfirmIngestionHttpError,
} from "../utils/documentManagementUserMessages";

export const DocumentManagement = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { httpGet, httpPost, httpPostFormData, httpPut, httpDelete, handleAlertBar } =
    useAPI();
  const handleAlertBarRef = useRef(handleAlertBar);
  handleAlertBarRef.current = handleAlertBar;
  const dmAlert = useCallback(
    (type: "success" | "error" | "warning" | "info", message: string) => {
      const text = message.trim();
      if (text) {
        handleAlertBarRef.current(type, text);
      }
    },
    []
  );
  const fileRef = useRef<HTMLInputElement>(null);
  const videoFileRef = useRef<HTMLInputElement>(null);
  const dirRef = useRef<HTMLInputElement>(null);

  const [uploadMediaMode, setUploadMediaMode] =
    useState<UploadMediaMode>("document");
  const [documentFiles, setDocumentFiles] = useState<File[]>([]);
  const [videoFiles, setVideoFiles] = useState<File[]>([]);
  const files =
    uploadMediaMode === "document" ? documentFiles : videoFiles;
  const setFilesForActiveMode = useCallback(
    (next: File[]) => {
      if (uploadMediaMode === "document") {
        setDocumentFiles(next);
      } else {
        setVideoFiles(next);
      }
    },
    [uploadMediaMode]
  );
  const [apiDatasets, setApiDatasets] = useState<DatasetOptionUi[]>([]);
  const [datasetSelection, setDatasetSelection] = useState<number | "">("");
  const [datasetTargetMode, setDatasetTargetMode] = useState<"existing" | "new">(
    "existing"
  );
  const [newDatasetName, setNewDatasetName] = useState("");
  const [newDatasetDescription, setNewDatasetDescription] = useState("");
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameBusy, setRenameBusy] = useState(false);
  const [renameName, setRenameName] = useState("");
  const [renameDescription, setRenameDescription] = useState("");
  const [roleOptions, setRoleOptions] = useState<RoleOptionUi[]>([]);
  const [uploadRoleAccess, setUploadRoleAccess] = useState<string[]>(["*"]);
  const [uploadCategory, setUploadCategory] = useState("");
  const [busy, setBusy] = useState(false);
  const [savingReview, setSavingReview] = useState(false);
  const [confirmBatchProgress, setConfirmBatchProgress] = useState<{
    completed: number;
    total: number;
  } | null>(null);
  const [previewItems, setPreviewItems] = useState<PreviewItemUi[] | null>(
    null
  );
  /** When false and preview items exist, show review instead of the upload form. */
  const [uploadFormVisible, setUploadFormVisible] = useState(true);
  const [uploadFailures, setUploadFailures] = useState<RagUploadFailureApiItem[]>(
    []
  );
  const pendingReviewBootstrapped = useRef(false);
  const persistedIngestSession = readPersistedIngestSession();
  /** Confirmed-and-enqueued ids: excluded from server preview rehydrate until session ends. */
  const [excludedFromReviewIds, setExcludedFromReviewIds] = useState<number[]>(
    () => persistedIngestSession?.excludedDocumentIds ?? []
  );
  const [confirmEnqueueFailures, setConfirmEnqueueFailures] = useState<
    ConfirmEnqueueFailureUi[]
  >([]);
  const [listNameFilter, setListNameFilter] = useState("");
  const [libraryTableActions, setLibraryTableActions] =
    useState<TableDataActionsModel>({
      page: 0,
      perPageRows: 25,
      order: "asc",
      orderBy: "original_file_name",
      searchText: "",
    });
  const [documents, setDocuments] = useState<DocumentRowUi[]>([]);
  const [deleteConfirm, setDeleteConfirm] = useState<DeleteConfirmState | null>(
    null
  );
  const [selectedDocumentIds, setSelectedDocumentIds] = useState<number[]>([]);
  const [selectedPreviewDocumentIds, setSelectedPreviewDocumentIds] = useState<
    number[]
  >([]);
  const [bulkDeleteBusy, setBulkDeleteBusy] = useState(false);
  const [disableConfirm, setDisableConfirm] = useState<DocumentRowUi | null>(null);
  const [lifecycleBusyId, setLifecycleBusyId] = useState<number | null>(null);
  const [rolesBusyId, setRolesBusyId] = useState<number | null>(null);
  const [retryProcessingBusyId, setRetryProcessingBusyId] = useState<
    number | null
  >(null);
  const [dontShowDeleteConfirmAgain, setDontShowDeleteConfirmAgain] =
    useState(false);
  /** In-memory only: survives repeated deletes in this visit, cleared on refresh / new tab / login. */
  const [skipDeleteConfirmPreview, setSkipDeleteConfirmPreview] =
    useState(false);
  const [deletingDocumentId, setDeletingDocumentId] = useState<number | null>(
    null
  );
  const [ingestJobs, setIngestJobs] = useState<Record<number, IngestJobUi>>(
    () => persistedIngestSession?.ingestJobs ?? {}
  );
  const [metadataJobs, setMetadataJobs] = useState<Record<number, IngestJobUi>>(
    () => persistedIngestSession?.metadataJobs ?? {}
  );
  const ingestEventSourceRef = useRef<Map<number, EventSource>>(new Map());
  const ingestRetryTimersRef = useRef<Map<number, number>>(new Map());
  const ingestRetryCountRef = useRef<Map<number, number>>(new Map());
  const ingestPollingTimersRef = useRef<Map<number, number>>(new Map());
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  /** 0 = documents section, 1 = organization users */
  const [dmSectionTab, setDmSectionTab] = useState(0);
  /** 0 = upload, 1 = document library (within documents section) */
  const [dmDocumentsSubTab, setDmDocumentsSubTab] = useState(() => {
    const sub = (location.state as { dmDocumentsSubTab?: number } | null)
      ?.dmDocumentsSubTab;
    return sub === 1 ? 1 : 0;
  });
  const [libraryMediaFilter, setLibraryMediaFilter] =
    useState<LibraryMediaFilter>("document");
  const libraryHeaders = useDocumentLibraryHeaders(libraryMediaFilter);

  const dmTabsSx = {
    borderBottom: 1,
    borderColor: "divider",
    "& .MuiTab-root": { textTransform: "none" as const, fontWeight: 600 },
  };

  useEffect(() => {
    if (previewItems === null) {
      setSkipDeleteConfirmPreview(false);
    }
  }, [previewItems]);

  /** Switch to Upload only when a new review batch starts — not on every preview merge during metadata. */
  const previewBatchActiveRef = useRef((previewItems?.length ?? 0) > 0);
  useEffect(() => {
    const active = (previewItems?.length ?? 0) > 0;
    if (active && !previewBatchActiveRef.current) {
      setDmDocumentsSubTab(0);
    }
    previewBatchActiveRef.current = active;
  }, [previewItems]);

  useEffect(() => {
    if (
      !previewItems?.length &&
      !Object.keys(ingestJobs).length &&
      !hasPersistedMetadataSession(metadataJobs)
    ) {
      setUploadFormVisible(true);
    }
  }, [previewItems, ingestJobs, metadataJobs]);

  useEffect(() => {
    if (loadingDocuments) {
      return;
    }
    const hasActiveIngestion = Object.values(ingestJobs).some(isIngestJobActive);
    /** Post-confirm ingestion only; metadata jobs must not block review rehydrate. */
    const hasPostConfirmIngestSession =
      hasActiveIngestion ||
      Object.keys(ingestJobs).length > 0 ||
      excludedFromReviewIds.length > 0;
    const excludedSet = new Set(excludedFromReviewIds);
    const docsForReview = documents.filter((d) => !excludedSet.has(d.id));
    const pendingBase = previewItemsFromPendingDocuments(docsForReview);
    const persistedSession = readPersistedIngestSession();
    const baseJobs =
      Object.keys(metadataJobs).length > 0
        ? metadataJobs
        : persistedSession?.metadataJobs ?? {};
    const jobByDocumentId = resolveMetadataJobByDocumentId(
      pendingBase,
      baseJobs,
      excludedFromReviewIds,
      persistedSession?.metadataJobByDocumentId
    );
    const jobsForResolve = mergeMetadataJobsFromDocumentMap(
      pendingBase,
      baseJobs,
      jobByDocumentId,
      excludedFromReviewIds
    );
    const pending = applyPersistedMetadataJobIds(pendingBase, jobByDocumentId);
    if (pending.length && !hasPostConfirmIngestSession) {
      if (
        metadataJobsKeySignature(jobsForResolve) !==
        metadataJobsKeySignature(metadataJobs)
      ) {
        setMetadataJobs(jobsForResolve);
      }
      setPreviewItems((prev) => mergePreviewItems(prev, pending));
      pendingReviewBootstrapped.current = true;
      setUploadFormVisible(false);
      return;
    }
    setPreviewItems((prev) => {
      if (!prev?.length) {
        return null;
      }
      const docById = new Map(documents.map((d) => [d.id, d]));
      const next = prev.filter((p) => {
        if (excludedSet.has(p.document_id)) {
          return false;
        }
        const row = docById.get(p.document_id);
        return row != null && isPendingReviewUpload(row.upload_status);
      });
      return next.length ? next : null;
    });
  }, [documents, loadingDocuments, ingestJobs, metadataJobs, excludedFromReviewIds]);

  useEffect(() => {
    setLibraryTableActions((prev) => ({ ...prev, page: 0 }));
  }, [listNameFilter]);

  const httpGetRef = useRef(httpGet);
  httpGetRef.current = httpGet;
  const documentsFetchInFlight = useRef(false);
  const metadataEventSourceRef = useRef<Map<number, EventSource>>(new Map());
  const metadataPollingTimersRef = useRef<Map<number, number>>(new Map());
  const metadataRetryTimersRef = useRef<Map<number, number>>(new Map());
  const metadataRetryCountRef = useRef<Map<number, number>>(new Map());
  const metadataJobsRef = useRef(metadataJobs);
  const ingestJobsRef = useRef(ingestJobs);
  const previewItemsRef = useRef(previewItems);
  const excludedFromReviewIdsRef = useRef(excludedFromReviewIds);
  const ingestDocumentsRefreshTimerRef = useRef<number | null>(null);
  /** Prevents duplicate GET documents after all jobs in a batch reach a terminal state. */
  const metadataBatchRefreshKeyRef = useRef<string | null>(null);
  const metadataPreviewRefreshInFlightRef = useRef(false);
  const metadataJobHydrateRequestedRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    metadataJobsRef.current = metadataJobs;
  }, [metadataJobs]);

  useEffect(() => {
    ingestJobsRef.current = ingestJobs;
  }, [ingestJobs]);

  useEffect(() => {
    previewItemsRef.current = previewItems;
  }, [previewItems]);

  useEffect(() => {
    excludedFromReviewIdsRef.current = excludedFromReviewIds;
  }, [excludedFromReviewIds]);

  const resetMetadataBatchTracking = useCallback(() => {
    metadataBatchRefreshKeyRef.current = null;
    metadataJobHydrateRequestedRef.current.clear();
  }, []);

  const refreshDocuments = useCallback(async () => {
    if (documentsFetchInFlight.current) {
      return;
    }
    documentsFetchInFlight.current = true;
    setLoadingDocuments(true);
    try {
      const response = await httpGetRef.current(RAG_API.documents, undefined, false);
      if (response.status === 403) {
        dmAlert("error",
          t("opva.dmForbidden", {
            defaultValue: "Org Admin role is required.",
          })
        );
        return;
      }
      if (response.status >= 400) {
        dmAlert("error",
          springErrorMessage(
            response.data,
            response.status,
            t("opva.dmDocumentsLoadFailed", {
              defaultValue: "Could not load documents.",
            })
          )
        );
        return;
      }
      const body = response.data as RagDocumentListResponse;
      if (body?.items && Array.isArray(body.items)) {
        setDocuments(body.items.map(mapDocumentListItem));
      }
    } catch (loadErr) {
      dmAlert("error",
        extractApiErrorMessage(
          loadErr,
          t("opva.dmDocumentsLoadFailed", {
            defaultValue: "Could not load documents.",
          })
        )
      );
    } finally {
      setLoadingDocuments(false);
      documentsFetchInFlight.current = false;
    }
  }, [dmAlert, t]);

  const scheduleIngestDocumentsRefresh = useCallback(() => {
    if (ingestDocumentsRefreshTimerRef.current != null) {
      window.clearTimeout(ingestDocumentsRefreshTimerRef.current);
    }
    ingestDocumentsRefreshTimerRef.current = window.setTimeout(() => {
      ingestDocumentsRefreshTimerRef.current = null;
      void refreshDocuments();
    }, 400);
  }, [refreshDocuments]);

  useEffect(() => {
    if (DOCUMENT_MANAGEMENT_SHOW_USERS_TAB && dmSectionTab !== 0) {
      return;
    }
    void refreshDocuments();
  }, [dmSectionTab, refreshDocuments]);

  useEffect(() => {
    const needsPoll = documents.some((d) =>
      isDocumentProcessing(d.upload_status, d.lifecycle_status)
    );
    if (!needsPoll) {
      return;
    }
    const timerId = window.setInterval(() => {
      void refreshDocuments();
    }, 5000);
    return () => window.clearInterval(timerId);
  }, [documents, refreshDocuments]);

  const clearMetadataJobTimers = useCallback((jobId: number) => {
    const pollingTimer = metadataPollingTimersRef.current.get(jobId);
    if (pollingTimer != null) {
      window.clearTimeout(pollingTimer);
      metadataPollingTimersRef.current.delete(jobId);
    }
    const retryTimer = metadataRetryTimersRef.current.get(jobId);
    if (retryTimer != null) {
      window.clearTimeout(retryTimer);
      metadataRetryTimersRef.current.delete(jobId);
    }
  }, []);

  const closeMetadataJobStream = useCallback(
    (jobId: number) => {
      const existing = metadataEventSourceRef.current.get(jobId);
      if (existing) {
        existing.close();
        metadataEventSourceRef.current.delete(jobId);
      }
      clearMetadataJobTimers(jobId);
    },
    [clearMetadataJobTimers]
  );

  const closeAllMetadataJobStreams = useCallback(() => {
    metadataEventSourceRef.current.forEach((es, jobId) => {
      es.close();
      clearMetadataJobTimers(jobId);
    });
    metadataEventSourceRef.current.clear();
    metadataRetryCountRef.current.clear();
  }, [clearMetadataJobTimers]);

  useEffect(() => {
    return () => {
      closeAllMetadataJobStreams();
    };
  }, [closeAllMetadataJobStreams]);

  useEffect(() => {
    httpGet(RAG_API.datasets, undefined, false)
      .then((response) => {
        if (response.status === 404) {
          dmAlert("error",
            springErrorMessage(
              response.data,
              404,
              "RAG datasets API not found."
            )
          );
          return;
        }
        if (response.status === 403) {
          dmAlert("error",
            springErrorMessage(response.data, 403, "Org Admin role required.")
          );
          return;
        }
        if (!response?.data || response.data?.error) {
          return;
        }
        const list = response.data as DatasetOptionUi[];
        if (Array.isArray(list) && list.length) {
          setApiDatasets(list);
          setDatasetSelection((prev) =>
            prev === "" || !list.some((d) => d.id === prev) ? list[0].id : prev
          );
          setDatasetTargetMode("existing");
        } else if (Array.isArray(list)) {
          setApiDatasets([]);
          setDatasetTargetMode("new");
          setDatasetSelection("");
        }
      })
      .catch((err) => {
        dmAlert("error",
          extractApiErrorMessage(
            err,
            "Could not load datasets. Is instrakt running on http://localhost:8080 with profile local?"
          )
        );
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- httpGet identity unstable
  }, []);

  useEffect(() => {
    httpGet(OPTIVAL_CONSTANTS.API_URLS.GET_ROLES, undefined, false)
      .then((response) => {
        if (!response?.data || response.data?.error) {
          return;
        }
        const raw = response.data as Array<{
          applicationRole?: boolean;
          id: number;
          name: string;
        }>;
        const roles = raw
          .filter((r) => !r.applicationRole)
          .map((r) => ({ id: r.id, name: r.name }));
        if (roles.length) {
          setRoleOptions(roles);
        }
      })
      .catch(() => {
        /* roles list is optional for upload — all-roles default still works */
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const backToOrganisation = () => {
    navigate("/management");
  };

  const handleUploadBoxDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const applyUploadFileSelection = (incoming: File[]) => {
    const { accepted: modeAccepted, rejected: wrongType } =
      filterFilesForUploadMode(incoming, uploadMediaMode);
    if (wrongType.length > 0) {
      dmAlert(
        "error",
        uploadMediaMode === "video"
          ? t("opva.dmUploadRejectedNotVideo", {
              defaultValue:
                "Only video files (MP4, MOV, AVI, MKV, WebM) can be added on the Videos tab. Skipped: {{names}}",
              names: wrongType.slice(0, 5).join(", "),
            })
          : t("opva.dmUploadRejectedNotPdf", {
              defaultValue:
                "Only PDF files can be added on the Documents tab. Skipped: {{names}}",
              names: wrongType.slice(0, 5).join(", "),
            })
      );
    }
    if (modeAccepted.length === 0) {
      return;
    }
    const {
      files: accepted,
      skipped,
      duplicateError,
      sizeError,
      sizeErrorKey,
    } = normalizeUploadFileSelection(modeAccepted, uploadMediaMode);
    if (duplicateError) {
      dmAlert("error", duplicateError);
      setFilesForActiveMode([]);
      return;
    }
    if (sizeError && sizeErrorKey) {
      dmAlert(
        "error",
        t(sizeErrorKey, { defaultValue: sizeError })
      );
      setFilesForActiveMode([]);
      return;
    }
    if (skipped.length > 0) {
      dmAlert(
        "info",
        t("opva.dmUploadSkipped", {
          defaultValue: "Skipped {{count}} system or empty file(s).",
          count: skipped.length,
        })
      );
    }
    setFilesForActiveMode(accepted);
  };

  const handleUploadBoxDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const dropped = e.dataTransfer.files;
    if (dropped?.length) applyUploadFileSelection(Array.from(dropped));
  };

  const handleDmFileChange: React.ChangeEventHandler<HTMLInputElement> = (
    event
  ) => {
    if (event.target.files?.length) {
      applyUploadFileSelection(Array.from(event.target.files));
    }
    event.target.value = "";
  };

  const handleDmFolderChange: React.ChangeEventHandler<HTMLInputElement> = (
    event
  ) => {
    if (event.target.files?.length) {
      applyUploadFileSelection(Array.from(event.target.files));
    }
    event.target.value = "";
  };

  const handleRemoveDmFile = (index: number) => {
    if (uploadMediaMode === "document") {
      setDocumentFiles((prev) => prev.filter((_, i) => i !== index));
    } else {
      setVideoFiles((prev) => prev.filter((_, i) => i !== index));
    }
  };

  const executeDeletePreviewItem = async (
    idx: number,
    documentId: number,
    fileName: string
  ) => {
    setDeletingDocumentId(documentId);
    try {
      const response = await httpDelete(
        RAG_API.document(documentId),
        undefined,
        undefined,
        false
      );
      if (response.status === 403) {
        dmAlert("error",
          t("opva.dmForbidden", {
            defaultValue: "Org Admin role is required to delete documents.",
          })
        );
        return;
      }
      if (response.status === 404) {
        dmAlert("error",
          springErrorMessage(
            response.data,
            404,
            t("opva.dmDocumentNotFound", {
              defaultValue: "Document not found on the server.",
            })
          )
        );
        return;
      }
      if (response.status >= 400) {
        dmAlert("error",
          springErrorMessage(
            response.data,
            response.status,
            t("opva.dmDeleteFailed", {
              defaultValue: "Could not delete the document.",
            })
          )
        );
        return;
      }
      removePreviewDocumentsFromSession([documentId]);
      await refreshDocuments();
      dmAlert("success",
        t("opva.dmDeleteSuccess", {
          defaultValue:
            '"{{name}}" was deleted. You can upload it again if needed.',
          name: fileName,
        })
      );
    } catch (deleteErr) {
      dmAlert("error",
        extractApiErrorMessage(
          deleteErr,
          t("opva.dmDeleteFailed", {
            defaultValue: "Could not delete the document.",
          })
        )
      );
    } finally {
      setDeletingDocumentId(null);
    }
  };

  const requestDeletePreviewItem = (idx: number) => {
    const item = previewItems?.[idx];
    if (!item) return;
    if (skipDeleteConfirmPreview) {
      void executeDeletePreviewItem(idx, item.document_id, item.file_name);
      return;
    }
    setDontShowDeleteConfirmAgain(false);
    setDeleteConfirm({
      source: "preview",
      idx,
      documentId: item.document_id,
      fileName: item.file_name,
    });
  };

  const requestDeleteListDocument = (row: DocumentRowUi) => {
    setDeleteConfirm({
      source: "list",
      documentId: row.id,
      fileName: row.original_file_name,
    });
  };

  const executeDeleteListDocument = async (documentId: number, fileName: string) => {
    setDeletingDocumentId(documentId);
    try {
      const response = await httpDelete(
        RAG_API.document(documentId),
        undefined,
        undefined,
        false
      );
      if (response.status === 403) {
        dmAlert("error",
          t("opva.dmForbidden", {
            defaultValue: "Org Admin role is required to delete documents.",
          })
        );
        return;
      }
      if (response.status >= 400) {
        dmAlert("error",
          springErrorMessage(
            response.data,
            response.status,
            t("opva.dmDeleteFailed", {
              defaultValue: "Could not delete the document.",
            })
          )
        );
        return;
      }
      await refreshDocuments();
      dmAlert("success",
        t("opva.dmDeleteSuccess", {
          defaultValue:
            '"{{name}}" was deleted. You can upload it again if needed.',
          name: fileName,
        })
      );
    } catch (deleteErr) {
      dmAlert("error",
        extractApiErrorMessage(
          deleteErr,
          t("opva.dmDeleteFailed", {
            defaultValue: "Could not delete the document.",
          })
        )
      );
    } finally {
      setDeletingDocumentId(null);
    }
  };

  const setDocumentActive = async (row: DocumentRowUi, active: boolean) => {
    const previousActive = row.is_active;
    setLifecycleBusyId(row.id);
    setDocuments((prev) =>
      prev.map((doc) =>
        doc.id === row.id ? { ...doc, is_active: active } : doc
      )
    );
    try {
      const url = active
        ? RAG_API.documentEnable(row.id)
        : RAG_API.documentDisable(row.id);
      const response = await httpPut(url, {}, undefined, false);
      if (response.status === 403) {
        setDocuments((prev) =>
          prev.map((doc) =>
            doc.id === row.id ? { ...doc, is_active: previousActive } : doc
          )
        );
        dmAlert("error",
          t("opva.dmForbidden", {
            defaultValue: "Org Admin role is required to manage documents.",
          })
        );
        return;
      }
      if (response.status >= 400) {
        setDocuments((prev) =>
          prev.map((doc) =>
            doc.id === row.id ? { ...doc, is_active: previousActive } : doc
          )
        );
        dmAlert("error",
          springErrorMessage(
            response.data,
            response.status,
            active
              ? t("opva.dmEnableFailed", {
                  defaultValue: "Could not enable the document.",
                })
              : t("opva.dmDisableFailed", {
                  defaultValue: "Could not disable the document.",
                })
          )
        );
        return;
      }
      setDisableConfirm(null);
      await refreshDocuments();
      dmAlert("success",
        t("opva.dmStatusUpdated", {
          defaultValue: "Document status updated successfully.",
        })
      );
    } catch (lifecycleErr) {
      setDocuments((prev) =>
        prev.map((doc) =>
          doc.id === row.id ? { ...doc, is_active: previousActive } : doc
        )
      );
      dmAlert("error",
        extractApiErrorMessage(
          lifecycleErr,
          active
            ? t("opva.dmEnableFailed", {
                defaultValue: "Could not enable the document.",
              })
            : t("opva.dmDisableFailed", {
                defaultValue: "Could not disable the document.",
              })
        )
      );
    } finally {
      setLifecycleBusyId(null);
    }
  };

  const handleToggleActive = (row: DocumentRowUi) => {
    if (rowActionsDisabled(row.id)) return;
    if (row.is_active) {
      setDisableConfirm(row);
      return;
    }
    void setDocumentActive(row, true);
  };

  const saveDocumentRoles = async (
    row: DocumentRowUi,
    roleAccess: string[]
  ): Promise<boolean> => {
    const normalized = normalizeRoleAccessForSave(roleAccess, roleOptions);
    if (!normalized.length) {
      dmAlert(
        "error",
        t("opva.dmRoleRequired", {
          defaultValue: "Select at least one role or choose all roles.",
        })
      );
      return false;
    }
    const previousRoleAccess = row.role_access;
    const nextRoleAccess = JSON.stringify(normalized);
    setRolesBusyId(row.id);
    setDocuments((prev) =>
      prev.map((doc) =>
        doc.id === row.id ? { ...doc, role_access: nextRoleAccess } : doc
      )
    );
    try {
      const response = await httpPut(
        RAG_API.documentRoleAccess(row.id),
        buildRoleAccessPatchBody(normalized),
        undefined,
        false
      );
      if (response.status === 403) {
        setDocuments((prev) =>
          prev.map((doc) =>
            doc.id === row.id
              ? { ...doc, role_access: previousRoleAccess }
              : doc
          )
        );
        dmAlert(
          "error",
          t("opva.dmForbidden", {
            defaultValue: "Org Admin role is required to manage documents.",
          })
        );
        return false;
      }
      if (response.status >= 400) {
        setDocuments((prev) =>
          prev.map((doc) =>
            doc.id === row.id
              ? { ...doc, role_access: previousRoleAccess }
              : doc
          )
        );
        dmAlert(
          "error",
          springErrorMessage(
            response.data,
            response.status,
            t("opva.dmRoleUpdateFailed", {
              defaultValue: "Could not update document roles.",
            })
          )
        );
        return false;
      }
      await refreshDocuments();
      dmAlert(
        "success",
        t("opva.dmRolesUpdated", {
          defaultValue: "Document roles updated successfully.",
        })
      );
      return true;
    } catch (roleErr) {
      setDocuments((prev) =>
        prev.map((doc) =>
          doc.id === row.id ? { ...doc, role_access: previousRoleAccess } : doc
        )
      );
      dmAlert(
        "error",
        extractApiErrorMessage(
          roleErr,
          t("opva.dmRoleUpdateFailed", {
            defaultValue: "Could not update document roles.",
          })
        )
      );
      return false;
    } finally {
      setRolesBusyId(null);
    }
  };

  const rowActionsDisabled = (documentId: number) =>
    bulkDeleteBusy ||
    deletingDocumentId === documentId ||
    lifecycleBusyId === documentId ||
    rolesBusyId === documentId ||
    retryProcessingBusyId === documentId;

  const handleRetryRolledBackDocument = async (row: DocumentRowUi) => {
    if (
      !isDocumentRolledBack(row.upload_status, row.lifecycle_status) ||
      retryProcessingBusyId !== null
    ) {
      return;
    }
    setRetryProcessingBusyId(row.id);
    try {
      const response = await httpPost(
        RAG_API.confirmIngestion,
        buildReconfirmIngestionBodyFromDocument(row),
        undefined,
        false
      );
      if (response.status === 403) {
        dmAlert(
          "error",
          t("opva.dmForbidden", {
            defaultValue: "Org Admin role is required to update documents.",
          })
        );
        return;
      }
      if (response.status >= 400) {
        dmAlert(
          "error",
          friendlyConfirmIngestionHttpError(
            response.data,
            response.status,
            t,
            t("opva.dmRetryProcessingFailed", {
              defaultValue: "Could not restart processing.",
            })
          )
        );
        return;
      }

      const body = response.data as RagConfirmIngestionResponse;
      const confs = body.confirmations || [];
      applyConfirmChunkResults(confs);

      const enqueued = confs.filter((c) => c.ingestion_enqueued);
      const enqueueFailures = mapConfirmEnqueueFailures(confs);

      if (enqueued.length === 0) {
        const errLines = enqueueFailures.map((f) =>
          formatConfirmEnqueueFailureLine(f.fileName, f.error, t)
        );
        dmAlert(
          "error",
          errLines.length
            ? errLines.join("\n")
            : t("opva.dmRetryProcessingFailed", {
                defaultValue: "Could not restart processing.",
              })
        );
        return;
      }

      dmAlert(
        "success",
        t("opva.dmRetryProcessingQueued", {
          defaultValue: "Processing restarted for {{fileName}}.",
          fileName: row.original_file_name,
        })
      );
      if (enqueueFailures.length) {
        dmAlert(
          "warning",
          t("opva.dmPartialConfirmWarning", {
            defaultValue:
              "{{count}} file(s) could not start processing. See details below.",
            count: enqueueFailures.length,
          })
        );
      }

      setDmDocumentsSubTab(0);
      await refreshDocuments();
    } catch (retryErr) {
      dmAlert(
        "error",
        friendlyConfirmIngestionError(
          extractApiErrorMessage(
            retryErr,
            t("opva.dmRetryProcessingFailed", {
              defaultValue: "Could not restart processing.",
            })
          ),
          t
        )
      );
    } finally {
      setRetryProcessingBusyId(null);
    }
  };

  const handleViewEditDocument = (row: DocumentRowUi) => {
    navigate(`/edit-document?documentId=${row.id}`, {
      state: { document: row, returnSubTab: 1 },
    });
  };

  const hasActiveIngestion = useMemo(
    () => Object.values(ingestJobs).some(isIngestJobActive),
    [ingestJobs]
  );

  useEffect(() => {
    const payload = buildIngestSessionPayloadForPersist({
      previewItems,
      excludedDocumentIds: excludedFromReviewIds,
      ingestJobs,
      metadataJobs,
    });
    const visiblePreview = (previewItems ?? []).filter(
      (item) => !excludedFromReviewIds.includes(item.document_id)
    );
    if (
      visiblePreview.length > 0 &&
      metadataJobsKeySignature(payload.metadataJobs) !==
        metadataJobsKeySignature(metadataJobs)
    ) {
      setMetadataJobs(payload.metadataJobs);
      return;
    }

    if (
      excludedFromReviewIds.length === 0 &&
      Object.keys(ingestJobs).length === 0 &&
      Object.keys(payload.metadataJobs).length === 0 &&
      Object.keys(payload.metadataJobByDocumentId).length === 0 &&
      !previewHasActiveMetadataBatch(previewItems, excludedFromReviewIds)
    ) {
      clearPersistedIngestSession();
      return;
    }
    writePersistedIngestSession(payload);
  }, [excludedFromReviewIds, ingestJobs, metadataJobs, previewItems]);

  useEffect(() => {
    if (previewItems?.length || loadingDocuments) {
      return;
    }
    if (Object.keys(metadataJobs).length === 0) {
      return;
    }
    const excludedSet = new Set(excludedFromReviewIds);
    const hasPendingOnServer = documents.some(
      (row) =>
        !excludedSet.has(row.id) && isPendingReviewUpload(row.upload_status)
    );
    if (hasPendingOnServer) {
      return;
    }
    setMetadataJobs({});
    resetMetadataBatchTracking();
  }, [
    previewItems,
    metadataJobs,
    loadingDocuments,
    documents,
    excludedFromReviewIds,
    resetMetadataBatchTracking,
  ]);

  const goToAddDocument = () => {
    const visible =
      previewItems?.filter(
        (item) => !excludedFromReviewIds.includes(item.document_id)
      ) ?? [];
    const metadataBusy =
      Object.values(metadataJobs).some(isMetadataJobActive) ||
      (visible.length > 0 && previewItemsNeedMetadataWait(visible));
    if (hasActiveIngestion || metadataBusy) {
      setDmDocumentsSubTab(0);
      dmAlert(
        "warning",
        t("opva.dmWaitForProcessing", {
          defaultValue:
            "Wait for current documents to finish processing before uploading more.",
        })
      );
      return;
    }
    setDmDocumentsSubTab(0);
    setUploadMediaMode(libraryMediaFilter);
    finishIngestionSession();
    if (previewItems?.length) {
      setUploadFormVisible(true);
    }
  };

  const showUploadMoreForm = () => {
    if (hasActiveIngestion || metadataDetectionInProgress) {
      return;
    }
    setUploadFormVisible(true);
  };

  const startNewUploadSession = () => {
    finishIngestionSession();
    setUploadFailures([]);
    setUploadFormVisible(true);
    setPreviewItems(null);
    setDocumentFiles([]);
    setVideoFiles([]);
    if (fileRef.current) fileRef.current.value = "";
    if (videoFileRef.current) videoFileRef.current.value = "";
    if (dirRef.current) dirRef.current.value = "";
  };

  const dismissUploadFailures = () => {
    setUploadFailures([]);
  };

  const clearIngestionTimers = useCallback((jobId: number) => {
    const retryTimer = ingestRetryTimersRef.current.get(jobId);
    if (retryTimer != null) {
      window.clearTimeout(retryTimer);
      ingestRetryTimersRef.current.delete(jobId);
    }
    const pollingTimer = ingestPollingTimersRef.current.get(jobId);
    if (pollingTimer != null) {
      window.clearTimeout(pollingTimer);
      ingestPollingTimersRef.current.delete(jobId);
    }
  }, []);

  const closeIngestionStream = useCallback(
    (jobId: number) => {
      const existing = ingestEventSourceRef.current.get(jobId);
      if (existing) {
        existing.close();
        ingestEventSourceRef.current.delete(jobId);
      }
      clearIngestionTimers(jobId);
    },
    [clearIngestionTimers]
  );

  const closeAllIngestionStreams = useCallback(() => {
    ingestEventSourceRef.current.forEach((es, jobId) => {
      es.close();
      clearIngestionTimers(jobId);
    });
    ingestEventSourceRef.current.clear();
    ingestRetryCountRef.current.clear();
    if (ingestDocumentsRefreshTimerRef.current != null) {
      window.clearTimeout(ingestDocumentsRefreshTimerRef.current);
      ingestDocumentsRefreshTimerRef.current = null;
    }
  }, [clearIngestionTimers]);

  const finishIngestionSession = useCallback(() => {
    setExcludedFromReviewIds([]);
    clearPersistedIngestSession();
    setConfirmEnqueueFailures([]);
    setSelectedPreviewDocumentIds([]);
    closeAllIngestionStreams();
    setIngestJobs({});
    closeAllMetadataJobStreams();
    setMetadataJobs({});
    resetMetadataBatchTracking();
  }, [
    closeAllIngestionStreams,
    closeAllMetadataJobStreams,
    resetMetadataBatchTracking,
  ]);

  /** Clears terminal ingest session so Upload shows the form again (not Processing complete). */
  const dismissIngestionCompleteSession = useCallback(() => {
    const jobs = Object.values(ingestJobs);
    const hasPendingReviewItems =
      (previewItems ?? []).filter(
        (item) => !excludedFromReviewIds.includes(item.document_id)
      ).length > 0;
    const completeIdle =
      jobs.length > 0 &&
      jobs.every(isIngestJobTerminal) &&
      !jobs.some(isIngestJobActive) &&
      !hasPendingReviewItems;
    if (!completeIdle) {
      return;
    }
    finishIngestionSession();
    setUploadFormVisible(true);
    setPreviewItems(null);
    previewBatchActiveRef.current = false;
  }, [
    ingestJobs,
    previewItems,
    excludedFromReviewIds,
    finishIngestionSession,
  ]);

  const backToPendingReview = () => {
    setUploadFormVisible(false);
  };

  const viewDocumentsLibrary = () => {
    setDmDocumentsSubTab(1);
    void refreshDocuments();
  };

  const removePreviewDocumentsFromSession = useCallback(
    (documentIds: Iterable<number>) => {
      const idSet = new Set(documentIds);
      if (!idSet.size) {
        return;
      }
      const jobIdsToClose = new Set<number>();
      for (const item of previewItemsRef.current ?? []) {
        if (idSet.has(item.document_id) && item.metadata_job_id) {
          jobIdsToClose.add(item.metadata_job_id);
        }
      }
      for (const job of Object.values(metadataJobsRef.current)) {
        if (job.documentId != null && idSet.has(job.documentId)) {
          jobIdsToClose.add(job.jobId);
        }
      }
      jobIdsToClose.forEach((jobId) => {
        closeMetadataJobStream(jobId);
      });
      setPreviewItems((prev) => {
        if (!prev) {
          return null;
        }
        const next = prev.filter((p) => !idSet.has(p.document_id));
        return next.length ? next : null;
      });
      setSelectedPreviewDocumentIds((prev) =>
        prev.filter((id) => !idSet.has(id))
      );
      setMetadataJobs((prev) => {
        const next: Record<number, IngestJobUi> = {};
        for (const [key, job] of Object.entries(prev)) {
          const jobId = Number(key);
          if (jobIdsToClose.has(jobId)) {
            continue;
          }
          if (job.documentId != null && idSet.has(job.documentId)) {
            continue;
          }
          next[jobId] = job;
        }
        return next;
      });
      resetMetadataBatchTracking();
    },
    [closeMetadataJobStream, resetMetadataBatchTracking]
  );

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    const pending = deleteConfirm;
    if (pending.source === "preview" && dontShowDeleteConfirmAgain) {
      setSkipDeleteConfirmPreview(true);
    }
    setDeleteConfirm(null);
    setDontShowDeleteConfirmAgain(false);
    if (pending.source === "preview") {
      await executeDeletePreviewItem(
        pending.idx,
        pending.documentId,
        pending.fileName
      );
    } else if (pending.source === "bulk") {
      await executeBulkDeleteListDocuments(pending.items);
    } else {
      await executeDeleteListDocument(pending.documentId, pending.fileName);
    }
  };

  const closeDeleteConfirmDialog = () => {
    if (deletingDocumentId !== null || bulkDeleteBusy) return;
    setDeleteConfirm(null);
    setDontShowDeleteConfirmAgain(false);
  };

  const updatePreviewMetadata = (
    idx: number,
    field: "company_name" | "product_name" | "model_name",
    value: string
  ) => {
    setPreviewItems((prev) => {
      if (!prev) return prev;
      return prev.map((item, i) =>
        i === idx
          ? {
              ...item,
              [field]: value,
              metadata_edited: { ...item.metadata_edited, [field]: true },
            }
          : item
      );
    });
  };

  const updatePreviewCategory = (idx: number, documentCategory: string) => {
    setPreviewItems((prev) => {
      if (!prev) return prev;
      return prev.map((item, i) =>
        i === idx ? { ...item, document_category: documentCategory } : item
      );
    });
  };

  const activeDataset = useMemo(() => {
    if (datasetTargetMode === "new") return undefined;
    if (datasetSelection === "") return undefined;
    return findDatasetById(datasetSelection, apiDatasets);
  }, [datasetTargetMode, datasetSelection, apiDatasets]);

  const openRenameDataset = () => {
    if (!activeDataset) return;
    setRenameName(activeDataset.name);
    setRenameDescription(activeDataset.description ?? "");
    setRenameOpen(true);
  };

  const submitRenameDataset = async () => {
    if (!activeDataset || !renameName.trim()) return;
    setRenameBusy(true);
    try {
      const response = await httpPut(
        RAG_API.dataset(activeDataset.id),
        {
          name: renameName.trim(),
          description: renameDescription.trim() || null,
        },
        undefined,
        false
      );
      if (response.status === 403) {
        dmAlert("error",
          t("opva.dmForbidden", {
            defaultValue: "Org Admin role is required.",
          })
        );
        return;
      }
      if (response.status === 409) {
        dmAlert("error",
          springErrorMessage(
            response.data,
            409,
            t("opva.dmDatasetRenameConflict", {
              defaultValue: "A dataset with this name already exists.",
            })
          )
        );
        return;
      }
      if (response.status >= 400 || response.data?.error) {
        dmAlert("error",
          springErrorMessage(
            response.data,
            response.status,
            t("opva.dmDatasetRenameFailed", {
              defaultValue: "Could not rename the dataset.",
            })
          )
        );
        return;
      }
      const listRes = await httpGet(RAG_API.datasets, undefined, false);
      if (Array.isArray(listRes.data)) {
        setApiDatasets(listRes.data as DatasetOptionUi[]);
      }
      setRenameOpen(false);
      dmAlert("success",
        t("opva.dmDatasetRenamed", {
          defaultValue: 'Dataset renamed to "{{name}}".',
          name: renameName.trim(),
        })
      );
    } catch (renameErr) {
      dmAlert("error",
        extractApiErrorMessage(
          renameErr,
          t("opva.dmDatasetRenameFailed", {
            defaultValue: "Could not rename the dataset.",
          })
        )
      );
    } finally {
      setRenameBusy(false);
    }
  };

  const mergePreviewFromDocuments = useCallback(
    async (items: PreviewItemUi[]): Promise<PreviewItemUi[]> => {
      const response = await httpGetRef.current(RAG_API.documents, undefined, false);
      if (response.status >= 400 || !response.data?.items) {
        return items;
      }
      const body = response.data as RagDocumentListResponse;
      const byId = new Map(
        body.items.map((row) => [row.id, mapDocumentListItem(row)])
      );
      return items.map((item) => {
        const row = byId.get(item.document_id);
        if (!row) {
          return item;
        }
        const mapped = mapDocumentRowToPreview(row);
        return preservePreviewSessionEdits(item, {
          ...mapped,
          dataset_id: item.dataset_id,
          dataset_name: item.dataset_name,
          document_category: item.document_category || mapped.document_category,
        });
      });
    },
    []
  );

  const getVisiblePreviewItemsForMetadata = useCallback((): PreviewItemUi[] => {
    const items = previewItemsRef.current;
    if (!items?.length) {
      return [];
    }
    const excluded = new Set(excludedFromReviewIdsRef.current);
    return items.filter((item) => !excluded.has(item.document_id));
  }, []);

  /** One documents fetch per batch when every metadata job is terminal and preview still shows pending. */
  const requestMetadataBatchPreviewRefresh = useCallback(async () => {
    const visible = getVisiblePreviewItemsForMetadata();
    if (!visible.length || !previewItemsNeedMetadataWait(visible)) {
      return;
    }
    const jobs = metadataJobsRef.current;
    const targets = collectMetadataJobTargets(visible, jobs);
    if (!targets.length) {
      return;
    }
    const allTerminal = targets.every((target) =>
      isMetadataJobTerminal(jobs[target.jobId]?.last)
    );
    if (!allTerminal) {
      return;
    }
    const batchKey = targets
      .map((t) => t.jobId)
      .sort((a, b) => a - b)
      .join(",");
    if (
      metadataBatchRefreshKeyRef.current === batchKey ||
      metadataPreviewRefreshInFlightRef.current
    ) {
      return;
    }
    metadataPreviewRefreshInFlightRef.current = true;
    try {
      const merged = await mergePreviewFromDocuments(visible);
      metadataBatchRefreshKeyRef.current = batchKey;
      setPreviewItems((prev) => {
        if (!prev?.length) {
          return merged;
        }
        const byDocId = new Map(merged.map((item) => [item.document_id, item]));
        return prev.map((item) => byDocId.get(item.document_id) ?? item);
      });
    } finally {
      metadataPreviewRefreshInFlightRef.current = false;
    }
  }, [getVisiblePreviewItemsForMetadata, mergePreviewFromDocuments]);

  const pollMetadataJobProgress = useCallback(
    async (jobId: number, fileName: string) => {
      try {
        const response = await httpGetRef.current(
          RAG_API.metadataJobStatus(jobId),
          undefined,
          false
        );
        if (response.status >= 400 || !response.data) {
          throw new Error("metadata job status unavailable");
        }
        const payload = response.data as IngestJobEventUi;
        setMetadataJobs((prev) => ({
          ...prev,
          [jobId]: {
            jobId,
            fileName,
            documentId: payload.document_id ?? prev[jobId]?.documentId,
            last: payload,
          },
        }));
        if (isMetadataJobTerminal(payload)) {
          closeMetadataJobStream(jobId);
          metadataRetryCountRef.current.delete(jobId);
          void requestMetadataBatchPreviewRefresh();
          return;
        }
      } catch {
        /* best-effort fallback */
      }
      const timerId = window.setTimeout(
        () => void pollMetadataJobProgress(jobId, fileName),
        5000
      );
      metadataPollingTimersRef.current.set(jobId, timerId);
    },
    [closeMetadataJobStream, requestMetadataBatchPreviewRefresh]
  );

  const startMetadataSSE = useCallback(
    (jobId: number, fileName: string) => {
      if (isMetadataJobTerminal(metadataJobsRef.current[jobId]?.last)) {
        return;
      }
      const existing = metadataEventSourceRef.current.get(jobId);
      if (
        existing &&
        existing.readyState !== EventSource.CLOSED
      ) {
        return;
      }
      closeMetadataJobStream(jobId);
      const url = metadataJobStreamUrl(jobId);
      const es = new EventSource(url);
      metadataEventSourceRef.current.set(jobId, es);

      es.onmessage = (ev) => {
        if (!ev.data) {
          return;
        }
        try {
          const payload = JSON.parse(ev.data) as IngestJobEventUi;
          if ((payload as { error?: string })?.error) {
            throw new Error((payload as { error?: string }).error);
          }
          setMetadataJobs((prev) => ({
            ...prev,
            [jobId]: {
              jobId,
              fileName,
              documentId: payload.document_id ?? prev[jobId]?.documentId,
              last: payload,
            },
          }));
          if (isMetadataJobTerminal(payload)) {
            closeMetadataJobStream(jobId);
            metadataRetryCountRef.current.delete(jobId);
            void requestMetadataBatchPreviewRefresh();
          }
        } catch {
          closeMetadataJobStream(jobId);
          void pollMetadataJobProgress(jobId, fileName);
        }
      };

      es.onerror = () => {
        if (isMetadataJobTerminal(metadataJobsRef.current[jobId]?.last)) {
          closeMetadataJobStream(jobId);
          return;
        }
        closeMetadataJobStream(jobId);
        const retryCount = (metadataRetryCountRef.current.get(jobId) ?? 0) + 1;
        metadataRetryCountRef.current.set(jobId, retryCount);
        if (retryCount > 4) {
          void pollMetadataJobProgress(jobId, fileName);
          return;
        }
        const delayMs = Math.min(2000 * retryCount, 10000);
        const timerId = window.setTimeout(
          () => startMetadataSSE(jobId, fileName),
          delayMs
        );
        metadataRetryTimersRef.current.set(jobId, timerId);
      };
    },
    [
      closeMetadataJobStream,
      pollMetadataJobProgress,
      requestMetadataBatchPreviewRefresh,
    ]
  );

  const finalizeUploadForReview = useCallback(
    async (
      items: PreviewItemUi[],
      responseFailures: RagUploadFailureApiItem[],
      filesStillPending: File[],
      resolved:
        | { kind: "new"; name: string; description: string; dataset?: { id: number; name: string } }
        | { kind: "existing"; dataset: DatasetOptionUi },
      resolvedDataset?: { id: number; name: string }
    ) => {
      if (items.length > 0) {
        resetMetadataBatchTracking();
        const merged = mergePreviewItems(previewItemsRef.current, items);
        const excluded = excludedFromReviewIdsRef.current;
        setMetadataJobs((prev) => {
          const next = mergeMetadataJobsForPreview(merged, prev, excluded);
          writePersistedIngestSession(
            buildIngestSessionPayloadForPersist({
              previewItems: merged,
              excludedDocumentIds: excluded,
              ingestJobs,
              metadataJobs: next,
            })
          );
          return next;
        });
        setPreviewItems(merged);
        setUploadFormVisible(false);
        setDmDocumentsSubTab(0);
        pendingReviewBootstrapped.current = true;
      }

      setFilesForActiveMode(filesStillPending);
      if (filesStillPending.length === 0) {
        if (fileRef.current) fileRef.current.value = "";
        if (dirRef.current) dirRef.current.value = "";
      }

      if (resolved.kind === "new" && resolvedDataset) {
        setNewDatasetName("");
        setNewDatasetDescription("");
        setDatasetTargetMode("existing");
        setDatasetSelection(resolvedDataset.id);
        try {
          const listRes = await httpGet(RAG_API.datasets, undefined, false);
          if (Array.isArray(listRes.data)) {
            setApiDatasets(listRes.data as DatasetOptionUi[]);
          }
        } catch {
          /* list refresh is best-effort */
        }
      }

      if (items.length > 0) {
        await refreshDocuments();
      }

      if (responseFailures.length > 0 && items.length > 0) {
        dmAlert(
          "success",
          t("opva.dmUploadSuccessCount", {
            defaultValue: "{{count}} file(s) uploaded. Review metadata below.",
            count: items.length,
          })
        );
        dmAlert(
          "warning",
          t("opva.dmUploadPartialWarning", {
            defaultValue:
              "{{count}} file(s) could not be uploaded. See details below.",
            count: responseFailures.length,
          })
        );
      } else if (items.length > 0) {
        setUploadFailures([]);
        dmAlert(
          "success",
          t("opva.dmUploadReviewHint", {
            defaultValue:
              "{{count}} file(s) uploaded. Review metadata below, then confirm when ready.",
            count: items.length,
          })
        );
      }
    },
    [dmAlert, httpGet, ingestJobs, refreshDocuments, resetMetadataBatchTracking, setFilesForActiveMode, t]
  );

  const onUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!files.length) return;
    const { duplicateError, sizeError, sizeErrorKey } =
      normalizeUploadFileSelection(files, uploadMediaMode);
    if (duplicateError) {
      dmAlert("error",duplicateError);
      return;
    }
    if (sizeError && sizeErrorKey) {
      dmAlert(
        "error",
        t(sizeErrorKey, { defaultValue: sizeError })
      );
      return;
    }
    const resolved = resolveUploadDatasetTarget(
      datasetTargetMode,
      datasetSelection,
      newDatasetName,
      newDatasetDescription,
      apiDatasets
    );
    if ("errorKey" in resolved) {
      dmAlert("error",
        t(resolved.errorKey, { defaultValue: resolved.defaultMessage })
      );
      return;
    }

    const roleAccess = normalizeRoleAccessForSave(
      uploadRoleAccess,
      roleOptions
    );
    if (!roleAccess.includes("*") && roleAccess.length === 0) {
      dmAlert("error",
        t("opva.dmRoleRequired", {
          defaultValue: "Select at least one role or choose all roles.",
        })
      );
      return;
    }

    setBusy(true);
    setUploadFailures([]);

    try {
      const uploadFormBase = {
        files,
        roleAccess,
        documentCategory: isDocumentCategorySelected(uploadCategory)
          ? uploadCategory
          : undefined,
      };
      const form =
        resolved.kind === "new"
          ? buildUploadFormData({
              newDatasetName: resolved.name,
              newDatasetDescription: resolved.description,
              ...uploadFormBase,
            })
          : buildUploadFormData({
              datasetId: resolved.dataset.id,
              ...uploadFormBase,
            });

      const response = await httpPostFormData(RAG_API.upload, form, true);

      if (response.status === 403) {
        dmAlert("error",
          t("opva.dmForbidden", {
            defaultValue: "Org Admin role is required to upload documents.",
          })
        );
        return;
      }
      if (response.status === 404) {
        dmAlert("error",
          springErrorMessage(
            response.data,
            404,
            "Upload API not found — rebuild and restart instrakt (profile local)."
          )
        );
        return;
      }
      if (response.status === 503) {
        dmAlert(
          "error",
          friendlyConfirmIngestionHttpError(
            response.data,
            503,
            t,
            t("opva.dmUploadFailed", { defaultValue: "Upload failed." })
          )
        );
        return;
      }
      if (response.status >= 400 || response.data?.error) {
        const message =
          (response.data as { message?: string })?.message ||
          springErrorMessage(
            response.data,
            response.status,
            t("opva.dmUploadFailed", { defaultValue: "Upload failed." })
          );
        dmAlert("error",message);
        return;
      }

      const body = response.data as RagUploadApiResponse;
      const responseFailures = body.failures ?? [];
      const uploadedDocs = body.documents ?? [];

      if (uploadedDocs.length === 0 && responseFailures.length > 0) {
        setUploadFailures(responseFailures);
        dmAlert(
          "error",
          t("opva.dmUploadAllFailed", {
            defaultValue: "No files were uploaded. See details below.",
          })
        );
        return;
      }

      const resolvedDataset = body.dataset;
      const datasetForUi: Pick<DatasetOptionUi, "id" | "name"> =
        resolvedDataset != null
          ? { id: resolvedDataset.id, name: resolvedDataset.name }
          : resolved.kind === "existing"
            ? { id: resolved.dataset.id, name: resolved.dataset.name }
            : { id: 0, name: resolved.name };

      const items = uploadedDocs.map((doc) => {
        const item = mapUploadItemToPreview(doc, datasetForUi);
        if (
          !item.document_category &&
          isDocumentCategorySelected(uploadCategory)
        ) {
          return { ...item, document_category: uploadCategory };
        }
        return item;
      });

      if (responseFailures.length > 0) {
        setUploadFailures(responseFailures);
      }

      const failedBasenames = new Set(
        responseFailures
          .map((f) => (f.file_name || "").toLowerCase())
          .filter(Boolean)
      );
      const filesStillPending = files.filter((f) =>
        failedBasenames.has(uploadBasename(f).toLowerCase())
      );

      const reviewItems = items;

      await finalizeUploadForReview(
        reviewItems,
        responseFailures,
        filesStillPending,
        resolved,
        resolvedDataset ?? undefined
      );
      if (uploadMediaMode === "document") {
        setDocumentFiles(filesStillPending);
      } else {
        setVideoFiles(filesStillPending);
      }
      if (filesStillPending.length === 0) {
        if (fileRef.current) fileRef.current.value = "";
        if (videoFileRef.current) videoFileRef.current.value = "";
        if (dirRef.current) dirRef.current.value = "";
      }

      if (resolved.kind === "new" && resolvedDataset) {
        setNewDatasetName("");
        setNewDatasetDescription("");
        setDatasetTargetMode("existing");
        setDatasetSelection(resolvedDataset.id);
        try {
          const listRes = await httpGet(RAG_API.datasets, undefined, false);
          if (Array.isArray(listRes.data)) {
            setApiDatasets(listRes.data as DatasetOptionUi[]);
          }
        } catch {
          /* list refresh is best-effort */
        }
      }

      if (items.length > 0) {
        await refreshDocuments();
      }

      if (responseFailures.length > 0 && items.length > 0) {
        dmAlert(
          "success",
          t("opva.dmUploadSuccessCount", {
            defaultValue: "{{count}} file(s) uploaded. Review metadata below.",
            count: items.length,
          })
        );
        dmAlert(
          "warning",
          t("opva.dmUploadPartialWarning", {
            defaultValue:
              "{{count}} file(s) could not be uploaded. See details below.",
            count: responseFailures.length,
          })
        );
      } else if (items.length > 0) {
        setUploadFailures([]);
        dmAlert(
          "success",
          t("opva.dmUploadReviewHint", {
            defaultValue:
              "{{count}} file(s) uploaded. Review metadata below, then confirm when ready.",
            count: items.length,
          })
        );
      }
    } catch (err) {
      dmAlert("error",
        extractApiErrorMessage(
          err,
          t("opva.dmUploadFailed", {
            defaultValue:
              "Upload failed. Check instrakt (profile local) on :8080 and the RAG worker on :8000.",
          })
        )
      );
    } finally {
      setBusy(false);
    }
  };

  const pollIngestionJob = useCallback(
    async (jobId: number, fileName: string) => {
      try {
        const response = await httpGetRef.current(
          RAG_API.ingestionJobStatus(jobId),
          undefined,
          false
        );
        if (response.status === 404) {
          const existing = ingestJobsRef.current[jobId];
          const removedEvent = buildRemovedDocumentIngestionJobEvent(
            jobId,
            existing?.documentId ?? existing?.last?.document_id
          );
          setIngestJobs((prev) => ({
            ...prev,
            [jobId]: {
              jobId,
              fileName,
              documentId: existing?.documentId ?? existing?.last?.document_id,
              last: removedEvent,
            },
          }));
          closeIngestionStream(jobId);
          ingestRetryCountRef.current.delete(jobId);
          scheduleIngestDocumentsRefresh();
          return;
        }
        if (response.status >= 400 || !response.data) {
          throw new Error("job status unavailable");
        }
        const payload = response.data as IngestJobUi["last"];
        setIngestJobs((prev) => ({
          ...prev,
          [jobId]: {
            jobId,
            fileName,
            documentId: payload?.document_id,
            last: payload,
          },
        }));
        if (payload?.status === "completed" || payload?.status === "failed") {
          closeIngestionStream(jobId);
          ingestRetryCountRef.current.delete(jobId);
          scheduleIngestDocumentsRefresh();
          return;
        }
      } catch {
        /* best-effort fallback */
      }
      const timerId = window.setTimeout(() => {
        void pollIngestionJob(jobId, fileName);
      }, 5000);
      ingestPollingTimersRef.current.set(jobId, timerId);
    },
    [closeIngestionStream, scheduleIngestDocumentsRefresh]
  );

  const startSSE = useCallback(
    (jobId: number, fileName: string) => {
      const tracked = ingestJobsRef.current[jobId];
      if (tracked && isIngestJobTerminal(tracked)) {
        return;
      }
      const existing = ingestEventSourceRef.current.get(jobId);
      if (existing && existing.readyState !== EventSource.CLOSED) {
        return;
      }
      closeIngestionStream(jobId);
      const url = ingestionJobStreamUrl(jobId);
      const es = new EventSource(url);
      ingestEventSourceRef.current.set(jobId, es);

      es.onmessage = (ev) => {
        if (!ev.data) {
          return;
        }
        try {
          const j = JSON.parse(ev.data) as IngestJobUi["last"];
          if ((j as { error?: string })?.error === "job not found") {
            const existing = ingestJobsRef.current[jobId];
            const removedEvent = buildRemovedDocumentIngestionJobEvent(
              jobId,
              existing?.documentId ?? existing?.last?.document_id
            );
            setIngestJobs((prev) => ({
              ...prev,
              [jobId]: {
                jobId,
                fileName,
                documentId: existing?.documentId ?? existing?.last?.document_id,
                last: removedEvent,
              },
            }));
            closeIngestionStream(jobId);
            ingestRetryCountRef.current.delete(jobId);
            scheduleIngestDocumentsRefresh();
            return;
          }
          if ((j as { error?: string })?.error) {
            throw new Error((j as { error?: string }).error);
          }
          setIngestJobs((prev) => ({
            ...prev,
            [jobId]: {
              jobId,
              fileName,
              documentId: j?.document_id,
              last: j,
            },
          }));
          if (j?.status === "completed" || j?.status === "failed") {
            closeIngestionStream(jobId);
            ingestRetryCountRef.current.delete(jobId);
            scheduleIngestDocumentsRefresh();
          }
        } catch {
          closeIngestionStream(jobId);
          void pollIngestionJob(jobId, fileName);
        }
      };

      es.onerror = () => {
        const job = ingestJobsRef.current[jobId];
        if (job && isIngestJobTerminal(job)) {
          closeIngestionStream(jobId);
          return;
        }
        closeIngestionStream(jobId);
        const retryCount = (ingestRetryCountRef.current.get(jobId) ?? 0) + 1;
        ingestRetryCountRef.current.set(jobId, retryCount);
        if (retryCount > 4) {
          void pollIngestionJob(jobId, fileName);
          return;
        }
        const delayMs = Math.min(2000 * retryCount, 10000);
        const timerId = window.setTimeout(() => startSSE(jobId, fileName), delayMs);
        ingestRetryTimersRef.current.set(jobId, timerId);
      };
    },
    [closeIngestionStream, pollIngestionJob, scheduleIngestDocumentsRefresh]
  );

  /** One SSE per active ingestion job — avoids duplicate opens from confirm + restore. */
  useEffect(() => {
    const jobs = Object.values(ingestJobs);
    if (!jobs.length) {
      return;
    }
    for (const job of jobs) {
      if (isIngestJobTerminal(job)) {
        if (ingestEventSourceRef.current.has(job.jobId)) {
          closeIngestionStream(job.jobId);
        }
        continue;
      }
      const es = ingestEventSourceRef.current.get(job.jobId);
      if (es && es.readyState !== EventSource.CLOSED) {
        continue;
      }
      startSSE(job.jobId, job.fileName);
    }
  }, [ingestJobs, startSSE, closeIngestionStream]);

  useEffect(() => {
    return () => {
      closeAllIngestionStreams();
    };
  }, [closeAllIngestionStreams]);

  const applyConfirmChunkResults = (confs: RagIngestionConfirmation[]) => {
    const chunkFailures = mapConfirmEnqueueFailures(confs);
    if (chunkFailures.length) {
      setConfirmEnqueueFailures((prev) => [...prev, ...chunkFailures]);
    }

    const enqueuedDocumentIds = confs
      .filter((c) => c.document_id && c.ingestion_enqueued)
      .map((c) => c.document_id as number);
    if (enqueuedDocumentIds.length) {
      setExcludedFromReviewIds((prev) => {
        const next = prev.slice();
        for (const id of enqueuedDocumentIds) {
          if (!next.includes(id)) {
            next.push(id);
          }
        }
        return next;
      });
    }
    setIngestJobs((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const c of confs) {
        if (c.job_id && c.document_id && c.ingestion_enqueued) {
          next[c.job_id] = {
            jobId: c.job_id,
            fileName: c.file_name || "",
            documentId: c.document_id,
            last: null,
          };
          changed = true;
        }
      }
      return changed ? next : prev;
    });
    setPreviewItems((prev) =>
      prev ? previewItemsAfterConfirm(prev, confs) : null
    );
  };

  const onConfirmPreview = async () => {
    if (!previewItems?.length) {
      setPreviewItems(null);
      return;
    }
    const itemsToConfirm =
      previewItems?.filter(
        (item) => !excludedFromReviewIds.includes(item.document_id)
      ) ?? [];
    if (!itemsToConfirm.length) {
      return;
    }
    const metadataBusy =
      Object.values(metadataJobs).some(isMetadataJobActive) ||
      previewItemsNeedMetadataWait(itemsToConfirm);
    if (metadataBusy) {
      dmAlert(
        "warning",
        t("opva.dmWaitForMetadata", {
          defaultValue:
            "Wait for metadata detection to finish before confirming.",
        })
      );
      return;
    }
    setConfirmEnqueueFailures([]);
    setSavingReview(true);
    setConfirmBatchProgress({ completed: 0, total: itemsToConfirm.length });
    const allConfs: RagIngestionConfirmation[] = [];
    try {
      const response = await httpPost(
        RAG_API.confirmIngestion,
        buildConfirmIngestionBody(itemsToConfirm),
        undefined,
        false
      );
      if (response.status === 403) {
        dmAlert(
          "error",
          t("opva.dmForbidden", {
            defaultValue: "Org Admin role is required to update documents.",
          })
        );
        return;
      }
      if (response.status >= 400) {
        dmAlert(
          "error",
          friendlyConfirmIngestionHttpError(
            response.data,
            response.status,
            t,
            t("opva.dmConfirmIngestionFailed", {
              defaultValue: "Could not start ingestion.",
            })
          )
        );
        return;
      }

      const body = response.data as RagConfirmIngestionResponse;
      const confs = body.confirmations || [];
      allConfs.push(...confs);
      applyConfirmChunkResults(confs);

      setConfirmBatchProgress({
        completed: itemsToConfirm.length,
        total: itemsToConfirm.length,
      });

      const ok = allConfs.filter((c) => c.ingestion_enqueued).length;
      const enqueueFailures = mapConfirmEnqueueFailures(allConfs);

      if (ok === 0) {
        const errLines = enqueueFailures.map((f) =>
          formatConfirmEnqueueFailureLine(f.fileName, f.error, t)
        );
        dmAlert(
          "error",
          errLines.length
            ? errLines.join("\n")
            : t("opva.dmConfirmIngestionFailed", {
                defaultValue: "Could not start ingestion.",
              })
        );
        return;
      }

      dmAlert(
        "success",
        t("opva.dmIngestionQueued", {
          defaultValue: "{{count}} file(s) queued for ingestion.",
          count: ok,
        })
      );
      if (enqueueFailures.length) {
        dmAlert(
          "warning",
          t("opva.dmPartialConfirmWarning", {
            defaultValue:
              "{{count}} file(s) could not start processing. See details below.",
            count: enqueueFailures.length,
          })
        );
      }

      setUploadFormVisible(false);
      setDmDocumentsSubTab(0);
      await refreshDocuments();
    } catch (confirmErr) {
      const queued = allConfs.filter((c) => c.ingestion_enqueued).length;
      const baseMessage = friendlyConfirmIngestionError(
        extractApiErrorMessage(
          confirmErr,
          t("opva.dmConfirmIngestionFailed", {
            defaultValue: "Could not start ingestion.",
          })
        ),
        t
      );
      dmAlert(
        "error",
        queued > 0
          ? t("opva.dmConfirmIngestionPartialHttpFailed", {
              defaultValue:
                "{{queued}} of {{total}} file(s) were queued before the request failed: {{message}}. Confirm the remaining files again.",
              queued,
              total: itemsToConfirm.length,
              message: baseMessage,
            })
          : baseMessage
      );
    } finally {
      setSavingReview(false);
      setConfirmBatchProgress(null);
    }
  };

  const activeJobs = useMemo(() => Object.values(ingestJobs), [ingestJobs]);

  const allJobsTerminal = useMemo(
    () =>
      activeJobs.length > 0 && activeJobs.every((job) => isIngestJobTerminal(job)),
    [activeJobs]
  );

  const pipelineDocumentCount = useMemo(
    () =>
      documents.filter((d) =>
        isDocumentIngestionPipeline(d.upload_status, d.lifecycle_status)
      ).length,
    [documents]
  );

  const awaitingDocumentValidation = useMemo(
    () => ingestJobsAwaitDocumentValidation(activeJobs, documents),
    [activeJobs, documents]
  );

  const allIngestOutcomesResolved = useMemo(() => {
    if (!allJobsTerminal || activeJobs.length === 0) {
      return false;
    }
    return !awaitingDocumentValidation;
  }, [allJobsTerminal, activeJobs.length, awaitingDocumentValidation]);

  const visiblePreviewItems = useMemo(
    () =>
      previewItems?.filter(
        (item) => !excludedFromReviewIds.includes(item.document_id)
      ) ?? null,
    [previewItems, excludedFromReviewIds]
  );

  const selectedPreviewIdSet = useMemo(
    () => new Set(selectedPreviewDocumentIds),
    [selectedPreviewDocumentIds]
  );

  useEffect(() => {
    const visibleIds = new Set(
      (visiblePreviewItems ?? []).map((item) => item.document_id)
    );
    setSelectedPreviewDocumentIds((prev) =>
      prev.filter((id) => visibleIds.has(id))
    );
  }, [visiblePreviewItems]);

  const togglePreviewDocumentSelect = (documentId: number) => {
    setSelectedPreviewDocumentIds((prev) =>
      prev.includes(documentId)
        ? prev.filter((id) => id !== documentId)
        : [...prev, documentId]
    );
  };

  const toggleSelectAllPreviewDocuments = () => {
    const items = visiblePreviewItems ?? [];
    if (!items.length) {
      return;
    }
    const allSelected = items.every((item) =>
      selectedPreviewIdSet.has(item.document_id)
    );
    if (allSelected) {
      setSelectedPreviewDocumentIds([]);
    } else {
      setSelectedPreviewDocumentIds(items.map((item) => item.document_id));
    }
  };

  /** Per-job metadata progress panel — includes preview job ids even before SSE updates state. */
  const metadataProgressJobs = useMemo(() => {
    if (!visiblePreviewItems?.length) {
      return Object.values(metadataJobs);
    }
    const targets = collectMetadataJobTargets(
      visiblePreviewItems,
      metadataJobs
    );
    return targets.map((target) => {
      const existing = metadataJobs[target.jobId];
      if (existing) {
        return existing;
      }
      return {
        jobId: target.jobId,
        fileName: target.fileName,
        documentId: target.documentId,
        last: null,
      };
    });
  }, [metadataJobs, visiblePreviewItems]);

  /** Attach SSE (or one-shot job status) for every batch metadata job — survives refresh/tab remount. */
  useEffect(() => {
    if (savingReview || !visiblePreviewItems?.length) {
      return;
    }
    const targets = collectMetadataJobTargets(visiblePreviewItems, metadataJobs);
    if (!targets.length) {
      return;
    }

    setMetadataJobs((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const target of targets) {
        const cur = next[target.jobId];
        if (!cur) {
          next[target.jobId] = {
            jobId: target.jobId,
            fileName: target.fileName,
            documentId: target.documentId,
            last: null,
          };
          changed = true;
          continue;
        }
        const fileName = target.fileName || cur.fileName;
        const documentId = target.documentId ?? cur.documentId;
        if (fileName !== cur.fileName || documentId !== cur.documentId) {
          next[target.jobId] = { ...cur, fileName, documentId };
          changed = true;
        }
      }
      return changed ? next : prev;
    });

    let allTerminal = targets.length > 0;
    for (const target of targets) {
      const job = metadataJobs[target.jobId];
      if (isMetadataJobTerminal(job?.last)) {
        if (!job?.last) {
          const hydrateRequested = metadataJobHydrateRequestedRef.current;
          if (!hydrateRequested.has(target.jobId)) {
            hydrateRequested.add(target.jobId);
            void pollMetadataJobProgress(target.jobId, target.fileName);
          }
        }
        continue;
      }
      allTerminal = false;
      const es = metadataEventSourceRef.current.get(target.jobId);
      if (es && es.readyState !== EventSource.CLOSED) {
        continue;
      }
      startMetadataSSE(target.jobId, target.fileName);
    }

    if (allTerminal) {
      void requestMetadataBatchPreviewRefresh();
    }
  }, [
    visiblePreviewItems,
    metadataJobs,
    savingReview,
    startMetadataSSE,
    pollMetadataJobProgress,
    requestMetadataBatchPreviewRefresh,
  ]);

  const metadataDetectionInProgress = useMemo(() => {
    if (!visiblePreviewItems?.length) {
      return false;
    }
    if (!previewItemsNeedMetadataWait(visiblePreviewItems)) {
      return false;
    }
    const targets = collectMetadataJobTargets(
      visiblePreviewItems,
      metadataJobs
    );
    if (!targets.length) {
      return true;
    }
    return targets.some(
      (target) => !isMetadataJobTerminal(metadataJobs[target.jobId]?.last)
    );
  }, [visiblePreviewItems, metadataJobs]);
  const hasPendingReview = (visiblePreviewItems?.length ?? 0) > 0;
  const hasOpenIngestSession =
    excludedFromReviewIds.length > 0 || activeJobs.length > 0;
  const showBackgroundPipelineProcessing =
    hasOpenIngestSession &&
    pipelineDocumentCount > 0 &&
    activeJobs.length === 0 &&
    !savingReview;
  const showIngestionValidating =
    activeJobs.length > 0 && allJobsTerminal && !allIngestOutcomesResolved;
  const showProcessingPanel =
    hasActiveIngestion ||
    savingReview ||
    showBackgroundPipelineProcessing ||
    showIngestionValidating;
  const showDonePanel =
    !hasActiveIngestion &&
    allIngestOutcomesResolved &&
    !hasPendingReview &&
    !awaitingDocumentValidation;

  const prevDmDocumentsSubTabRef = useRef(dmDocumentsSubTab);
  const prevDmSectionTabRef = useRef(dmSectionTab);
  useEffect(() => {
    const subTabChanged =
      prevDmDocumentsSubTabRef.current !== dmDocumentsSubTab;
    const sectionChanged = prevDmSectionTabRef.current !== dmSectionTab;
    prevDmDocumentsSubTabRef.current = dmDocumentsSubTab;
    prevDmSectionTabRef.current = dmSectionTab;
    if (!subTabChanged && !sectionChanged) {
      return;
    }
    dismissIngestionCompleteSession();
  }, [
    dmDocumentsSubTab,
    dmSectionTab,
    dismissIngestionCompleteSession,
  ]);

  const showReviewPanel =
    hasPendingReview &&
    !uploadFormVisible &&
    !showDonePanel &&
    !hasActiveIngestion &&
    !savingReview;
  const showMetadataDetectPhase = showReviewPanel && metadataDetectionInProgress;
  const showReviewMetadataTable =
    showReviewPanel &&
    !!visiblePreviewItems?.length &&
    !metadataDetectionInProgress;

  useEffect(() => {
    if (metadataDetectionInProgress) {
      return;
    }
    closeAllMetadataJobStreams();
  }, [metadataDetectionInProgress, closeAllMetadataJobStreams]);

  const showUploadPanel =
    !showProcessingPanel &&
    !showDonePanel &&
    !hasOpenIngestSession &&
    (uploadFormVisible || !hasPendingReview);

  const showPipelineBanner =
    dmDocumentsSubTab === 0 &&
    pipelineDocumentCount > 0 &&
    activeJobs.length === 0 &&
    !showProcessingPanel &&
    !showDonePanel;

  const uploadFailureDisplays = useMemo(
    () => mapUploadFailuresDisplay(uploadFailures),
    [uploadFailures]
  );

  const previewDatasetName = useMemo(() => {
    if (!previewItems?.length) return undefined;
    const names = new Set(
      previewItems.map((p) => p.dataset_name).filter((n) => n.trim().length > 0)
    );
    if (names.size === 1) {
      return Array.from(names)[0];
    }
    return undefined;
  }, [previewItems]);

  const librarySearchCandidates = useMemo(() => {
    const q = listNameFilter.trim().toLowerCase();
    return documents.filter(
      (row) =>
        !isPendingReviewUpload(row.upload_status) &&
        (!q || row.original_file_name.toLowerCase().includes(q))
    );
  }, [documents, listNameFilter]);

  const filteredDocuments = useMemo(
    () => filterLibraryRowsByMedia(librarySearchCandidates, libraryMediaFilter),
    [librarySearchCandidates, libraryMediaFilter]
  );

  const libraryDocumentCount = useMemo(
    () => filterLibraryRowsByMedia(librarySearchCandidates, "document").length,
    [librarySearchCandidates]
  );

  const libraryVideoCount = useMemo(
    () => filterLibraryRowsByMedia(librarySearchCandidates, "video").length,
    [librarySearchCandidates]
  );

  useEffect(() => {
    setLibraryTableActions((prev) => ({ ...prev, page: 0 }));
    setSelectedDocumentIds([]);
  }, [libraryMediaFilter]);

  const sortedDocuments = useMemo(() => {
    const copy = [...filteredDocuments];
    return tableSorting(
      copy,
      libraryTableActions.order,
      libraryTableActions.orderBy,
      documentLibrarySortPropertyType(libraryTableActions.orderBy)
    );
  }, [
    filteredDocuments,
    libraryTableActions.order,
    libraryTableActions.orderBy,
  ]);

  const visibleDocuments = useMemo(
    () =>
      sortedDocuments.slice(
        libraryTableActions.page * libraryTableActions.perPageRows,
        libraryTableActions.page * libraryTableActions.perPageRows +
          libraryTableActions.perPageRows
      ),
    [
      sortedDocuments,
      libraryTableActions.page,
      libraryTableActions.perPageRows,
    ]
  );

  const handleLibrarySort = (property: string) => {
    const isAsc =
      libraryTableActions.orderBy === property &&
      libraryTableActions.order === "asc";
    setLibraryTableActions((prev) => ({
      ...prev,
      order: isAsc ? "desc" : ("asc" as Order),
      orderBy: property,
    }));
  };

  const handleLibraryPageChange = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setLibraryTableActions((prev) => ({ ...prev, page: newPage }));
  };

  const handleLibraryRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setLibraryTableActions((prev) => ({
      ...prev,
      perPageRows: parseInt(event.target.value, 10),
      page: 0,
    }));
  };

  const selectedIdSet = useMemo(
    () => new Set(selectedDocumentIds),
    [selectedDocumentIds]
  );

  const toggleDocumentSelection = (documentId: number) => {
    setSelectedDocumentIds((prev) =>
      prev.includes(documentId)
        ? prev.filter((id) => id !== documentId)
        : [...prev, documentId]
    );
  };

  const allPageSelected =
    visibleDocuments.length > 0 &&
    visibleDocuments.every((row) => selectedIdSet.has(row.id));

  const somePageSelected = visibleDocuments.some((row) =>
    selectedIdSet.has(row.id)
  );

  const toggleSelectAllVisible = () => {
    const visibleIds = visibleDocuments.map((row) => row.id);
    if (allPageSelected) {
      const visibleIdSet = new Set(visibleIds);
      setSelectedDocumentIds((prev) =>
        prev.filter((id) => !visibleIdSet.has(id))
      );
    } else {
      setSelectedDocumentIds((prev) =>
        Array.from(new Set([...prev, ...visibleIds]))
      );
    }
  };

  const requestBulkDeleteListDocuments = () => {
    const items = sortedDocuments
      .filter((row) => selectedIdSet.has(row.id))
      .map((row) => ({
        documentId: row.id,
        fileName: row.original_file_name,
      }));
    if (!items.length) return;
    setDeleteConfirm({ source: "bulk", items });
  };

  const requestBulkDeletePreviewItems = () => {
    const items = (visiblePreviewItems ?? [])
      .filter((item) => selectedPreviewIdSet.has(item.document_id))
      .map((item) => ({
        documentId: item.document_id,
        fileName: item.file_name,
      }));
    if (!items.length) {
      return;
    }
    setDeleteConfirm({ source: "bulk", items });
  };

  const executeBulkDeleteListDocuments = async (
    items: { documentId: number; fileName: string }[]
  ) => {
    const ids = items.map((i) => i.documentId);
    if (!ids.length) {
      return;
    }
    setBulkDeleteBusy(true);
    const allDeletedIds = new Set<number>();
    let totalFailed = 0;
    let totalDeleted = 0;
    const failedLines: string[] = [];
    try {
      const idChunks = chunkArray(ids, RAG_BULK_DELETE_MAX_BATCH);
      for (const chunk of idChunks) {
        const response = await httpPost(
          RAG_API.documentsBulkDelete,
          buildBulkDeleteBody(chunk),
          undefined,
          false
        );
        if (response.status === 403) {
          dmAlert(
            "error",
            t("opva.dmForbidden", {
              defaultValue: "Org Admin role is required to delete documents.",
            })
          );
          return;
        }
        if (response.status >= 400) {
          dmAlert(
            "error",
            springErrorMessage(
              response.data,
              response.status,
              t("opva.dmBulkDeleteFailed", {
                defaultValue: "Bulk delete failed.",
              })
            )
          );
          return;
        }
        const body = response.data as RagBulkDeleteResponse;
        for (const r of body.results || []) {
          if (r.ok) {
            allDeletedIds.add(r.document_id);
          } else {
            failedLines.push(
              `${r.file_name || `#${r.document_id}`}: ${r.error || "failed"}`
            );
          }
        }
        totalFailed += body.failed_count ?? 0;
        totalDeleted += body.deleted_count ?? 0;
      }

      setSelectedDocumentIds((prev) =>
        prev.filter((id) => !allDeletedIds.has(id))
      );
      if (allDeletedIds.size > 0) {
        removePreviewDocumentsFromSession(Array.from(allDeletedIds));
      }
      await refreshDocuments();

      if (failedLines.length) {
        dmAlert("error", failedLines.join("\n"));
      }
      if (totalDeleted > 0) {
        dmAlert(
          "success",
          t("opva.dmBulkDeleteSuccess", {
            defaultValue: "Deleted {{count}} document(s).",
            count: totalDeleted,
          })
        );
      } else if (totalFailed > 0 && !failedLines.length) {
        dmAlert(
          "error",
          t("opva.dmBulkDeleteFailed", {
            defaultValue: "Bulk delete failed.",
          })
        );
      }
    } catch (bulkErr) {
      if (allDeletedIds.size > 0) {
        removePreviewDocumentsFromSession(Array.from(allDeletedIds));
        await refreshDocuments();
      }
      dmAlert(
        "error",
        extractApiErrorMessage(
          bulkErr,
          t("opva.dmBulkDeleteFailed", {
            defaultValue: "Bulk delete failed.",
          })
        )
      );
    } finally {
      setBulkDeleteBusy(false);
    }
  };

  return (
    <div className="document-management px-5 pt-2">
      <Breadcrumb
        pathList={[
          { name: t("opva.management"), path: "management", isLink: true },
          { name: t("opva.documentManagement"), path: "document-management" },
        ]}
      />
      <Grid container alignItems="center" className="pt-2 pb-4 mb-2">
        <Grid item xs md lg={6} className="d-flex align-items-center">
          <Link
            title={t("opva.backToManagement")}
            className="me-2"
            component="button"
            onClick={backToOrganisation}
          >
            <KeyboardArrowLeftIcon fontSize="large" />
          </Link>
          <Typography variant="h3" className="mb-0">
            {t("opva.documentManagement")}
          </Typography>
        </Grid>
        {(!DOCUMENT_MANAGEMENT_SHOW_USERS_TAB || dmSectionTab === 0) &&
        dmDocumentsSubTab === 1 ? (
          <Grid
            item
            xs
            md
            lg={6}
            className="d-flex align-items-center justify-content-end dm-library-toolbar__actions"
          >
            <Input
              id="search"
              placeholder={t("opva.searchByDocument", {
                defaultValue: "Search by document",
              })}
              value={listNameFilter}
              startAdornment={
                <InputAdornment position="start">
                  <SearchIcon color="primary" />
                </InputAdornment>
              }
              endAdornment={
                listNameFilter ? (
                  <InputAdornment position="end">
                    <Link component="button" title={t("opva.clear")}>
                      <CloseIcon
                        fontSize="small"
                        onClick={() => setListNameFilter("")}
                      />
                    </Link>
                  </InputAdornment>
                ) : null
              }
              aria-describedby="standard-weight-helper-text"
              onChange={(e) => setListNameFilter(e.target.value)}
            />
            <Button
              variant="contained"
              size="medium"
              className="px-4 ms-4 dm-library-toolbar__btn"
              disabled={hasActiveIngestion}
              onClick={goToAddDocument}
            >
              {t("opva.dmAddNewDocument", {
                defaultValue: "Add New Document",
              })}
            </Button>
            <Button
              variant="contained"
              color="error"
              size="medium"
              className="px-4 ms-4 dm-library-toolbar__btn"
              disabled={
                selectedDocumentIds.length === 0 ||
                bulkDeleteBusy ||
                loadingDocuments
              }
              onClick={requestBulkDeleteListDocuments}
            >
              {t("opva.dmDeleteDocuments", {
                defaultValue: "Delete Documents",
              })}
            </Button>
          </Grid>
        ) : null}
      </Grid>

      {DOCUMENT_MANAGEMENT_SHOW_USERS_TAB ? (
        <Tabs
          value={dmSectionTab}
          onChange={(_, v: number) => setDmSectionTab(v)}
          sx={{ mb: 1, ...dmTabsSx }}
          aria-label={t("opva.documentManagement")}
        >
          <Tab
            label={t("opva.dmDocumentsSectionTab", {
              defaultValue: "Documents",
            })}
          />
          <Tab label={t("opva.userManagement")} />
        </Tabs>
      ) : null}

      {DOCUMENT_MANAGEMENT_SHOW_USERS_TAB && dmSectionTab === 1 ? (
        <DocumentManagementUsersTab />
      ) : null}

      {!DOCUMENT_MANAGEMENT_SHOW_USERS_TAB || dmSectionTab === 0 ? (
        <>
          <Tabs
            value={dmDocumentsSubTab}
            onChange={(_, v: number) => setDmDocumentsSubTab(v)}
            className="dm-sub-tabs"
            sx={{ mb: 2, ...dmTabsSx }}
            aria-label={t("opva.dmDocumentsSubTabsAria", {
              defaultValue: "Documents section",
            })}
          >
            <Tab
              label={t("opva.dmUploadTab", { defaultValue: "Upload" })}
            />
            <Tab
              label={t("opva.dmLibraryTab", { defaultValue: "Documents" })}
            />
          </Tabs>

          {dmDocumentsSubTab === 0 ? (
            <>
              {showPipelineBanner ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {t("opva.dmPipelineBanner", {
                    defaultValue:
                      "{{count}} document(s) are still processing in the background. View them in the Documents tab.",
                    count: pipelineDocumentCount,
                  })}{" "}
                  <Button
                    size="small"
                    onClick={viewDocumentsLibrary}
                    sx={{ verticalAlign: "baseline", ml: 0.5 }}
                  >
                    {t("opva.dmViewDocuments", {
                      defaultValue: "View documents",
                    })}
                  </Button>
                </Alert>
              ) : null}

              {showUploadPanel ? (
                <Box component="section" className="dm-card">
                  {hasPendingReview ? (
                    <Box sx={{ mb: 2 }}>
                      <Button
                        variant="text"
                        size="small"
                        onClick={backToPendingReview}
                        sx={{ px: 0, minWidth: 0, fontWeight: 600 }}
                      >
                        {t("opva.dmBackToReview", {
                          defaultValue: "← Back to review",
                        })}
                      </Button>
                    </Box>
                  ) : null}
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 2,
                      fontWeight: 600,
                      minHeight: 32,
                      lineHeight: "32px",
                    }}
                  >
                    {uploadMediaMode === "video"
                      ? t("opva.dmUploadVideosSection", {
                          defaultValue: "Upload Videos",
                        })
                      : t("opva.dmUploadDocumentsSection", {
                          defaultValue: "Upload Documents",
                        })}
                  </Typography>
                  {uploadFailureDisplays.length > 0 ? (
                    <DocumentUploadFailuresAlert
                      failures={uploadFailureDisplays}
                      onDismiss={dismissUploadFailures}
                    />
                  ) : null}
                  <DocumentUploadPanel
                    onSubmit={onUpload}
                    uploadMediaMode={uploadMediaMode}
                    onUploadMediaModeChange={setUploadMediaMode}
                    files={files}
                    busy={busy}
                    fileInputRef={
                      uploadMediaMode === "document" ? fileRef : videoFileRef
                    }
                    folderInputRef={dirRef}
                    onDragOver={handleUploadBoxDragOver}
                    onDrop={handleUploadBoxDrop}
                    onFileChange={handleDmFileChange}
                    onFolderChange={handleDmFolderChange}
                    onRemoveFile={handleRemoveDmFile}
                    onSelectFolder={() => dirRef.current?.click()}
                    apiDatasets={apiDatasets}
                    datasetTargetMode={datasetTargetMode}
                    onDatasetTargetModeChange={(mode) => {
                      setDatasetTargetMode(mode);
                    }}
                    datasetSelection={datasetSelection}
                    onDatasetSelectionChange={setDatasetSelection}
                    newDatasetName={newDatasetName}
                    onNewDatasetNameChange={setNewDatasetName}
                    newDatasetDescription={newDatasetDescription}
                    onNewDatasetDescriptionChange={setNewDatasetDescription}
                    activeDataset={activeDataset}
                    onRenameDataset={openRenameDataset}
                    uploadRoleAccess={uploadRoleAccess}
                    onUploadRoleAccessChange={setUploadRoleAccess}
                    roleOptions={roleOptions}
                    uploadCategory={uploadCategory}
                    onUploadCategoryChange={setUploadCategory}
                  />
                </Box>
              ) : null}

              {showProcessingPanel && savingReview && activeJobs.length === 0 ? (
                <Box
                  component="section"
                  className="dm-ingest-panel"
                  sx={{ display: "flex", alignItems: "center", gap: 2 }}
                >
                  <CircularProgress size={28} />
                  <Typography variant="body2" color="text.secondary">
                    {t("opva.dmConfirmStarting", {
                      defaultValue: "Starting processing…",
                    })}
                  </Typography>
                </Box>
              ) : null}
              {showIngestionValidating && !hasActiveIngestion ? (
                <Box
                  component="section"
                  className="dm-ingest-panel"
                  sx={{ display: "flex", alignItems: "center", gap: 2 }}
                >
                  <CircularProgress size={28} />
                  <Typography variant="body2" color="text.secondary">
                    {t("opva.dmValidatingIngestion", {
                      defaultValue: "Validating document indexing…",
                    })}
                  </Typography>
                </Box>
              ) : null}
              {showProcessingPanel &&
              !(savingReview && activeJobs.length === 0) &&
              activeJobs.length > 0 &&
              !showIngestionValidating ? (
                <DocumentIngestionProgress
                  jobs={activeJobs}
                  confirmFailures={confirmEnqueueFailures}
                />
              ) : null}
              {showBackgroundPipelineProcessing ? (
                <Alert severity="info" sx={{ mb: 2 }} className="dm-ingest-panel">
                  {t("opva.dmBackgroundProcessing", {
                    defaultValue:
                      "{{count}} document(s) are still processing. Progress will update when you stay on this tab or refresh the library.",
                    count: pipelineDocumentCount,
                  })}
                </Alert>
              ) : null}

              {(showMetadataDetectPhase || showReviewMetadataTable) &&
              visiblePreviewItems ? (
                <Box>
                  {uploadFailureDisplays.length > 0 ? (
                    <DocumentUploadFailuresAlert
                      failures={uploadFailureDisplays}
                      onDismiss={dismissUploadFailures}
                    />
                  ) : null}
                  {showMetadataDetectPhase ? (
                    metadataProgressJobs.length > 0 ? (
                      <DocumentIngestionProgress
                        jobs={metadataProgressJobs}
                        title={t("opva.dmMetadataProgressTitle", {
                          defaultValue: "Detecting metadata",
                        })}
                        waitingLabel={t("opva.dmMetadataWaiting", {
                          defaultValue: "Waiting for metadata detection…",
                        })}
                      />
                    ) : (
                      <Box
                        component="section"
                        className="dm-ingest-panel"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          mb: 2,
                        }}
                      >
                        <CircularProgress size={28} />
                        <Typography variant="body2" color="text.secondary">
                          {t("opva.dmMetadataDetectingBatch", {
                            defaultValue:
                              "Detecting metadata for uploaded files…",
                          })}
                        </Typography>
                      </Box>
                    )
                  ) : null}
                  {showReviewMetadataTable ? (
                    <DocumentMetadataPreview
                      items={visiblePreviewItems}
                      activeDatasetName={previewDatasetName}
                      saving={savingReview}
                      deletingDocumentId={deletingDocumentId}
                      bulkDeleteBusy={bulkDeleteBusy}
                      selectedIdSet={selectedPreviewIdSet}
                      onToggleSelect={togglePreviewDocumentSelect}
                      onToggleSelectAll={toggleSelectAllPreviewDocuments}
                      onBulkDelete={requestBulkDeletePreviewItems}
                      onUpdateMetadata={updatePreviewMetadata}
                      onUpdateCategory={updatePreviewCategory}
                      onRemoveItem={requestDeletePreviewItem}
                      onUploadMore={showUploadMoreForm}
                      onConfirm={() => void onConfirmPreview()}
                      uploadMoreDisabled={hasActiveIngestion}
                    />
                  ) : null}
                </Box>
              ) : null}

              {showDonePanel ? (
                <DocumentIngestionDone
                  jobs={activeJobs}
                  documents={documents}
                  onUploadMore={startNewUploadSession}
                  onViewDocuments={viewDocumentsLibrary}
                />
              ) : null}
            </>
          ) : null}

          {dmDocumentsSubTab === 1 ? (
            <DocumentLibraryTable
              libraryMediaFilter={libraryMediaFilter}
              onLibraryMediaFilterChange={setLibraryMediaFilter}
              documentCount={libraryDocumentCount}
              videoCount={libraryVideoCount}
              visibleDocuments={visibleDocuments}
              totalFilteredCount={sortedDocuments.length}
              loadingDocuments={loadingDocuments}
              hasNameFilter={listNameFilter.trim().length > 0}
              tablePage={libraryTableActions.page}
              rowsPerPage={libraryTableActions.perPageRows}
              order={libraryTableActions.order}
              orderBy={libraryTableActions.orderBy}
              headers={libraryHeaders}
              selectedCount={selectedDocumentIds.length}
              allVisibleSelected={allPageSelected}
              someVisibleSelected={somePageSelected}
              bulkDeleteBusy={bulkDeleteBusy}
              selectedIdSet={selectedIdSet}
              onToggleSelectAll={toggleSelectAllVisible}
              onToggleRow={toggleDocumentSelection}
              onSort={handleLibrarySort}
              onPageChange={handleLibraryPageChange}
              onRowsPerPageChange={handleLibraryRowsPerPageChange}
              rowActionsDisabled={rowActionsDisabled}
              onViewEdit={handleViewEditDocument}
              onDelete={requestDeleteListDocument}
              onRetryProcessing={(row) => void handleRetryRolledBackDocument(row)}
              onToggleActive={handleToggleActive}
              roleOptions={roleOptions}
              rolesBusyId={rolesBusyId}
              onSaveDocumentRoles={saveDocumentRoles}
            />
          ) : null}
        </>
      ) : null}

      <DocumentManagementDialogs
        renameOpen={renameOpen}
        renameBusy={renameBusy}
        renameName={renameName}
        renameDescription={renameDescription}
        onRenameNameChange={setRenameName}
        onRenameDescriptionChange={setRenameDescription}
        onRenameClose={() => setRenameOpen(false)}
        onRenameSubmit={() => void submitRenameDataset()}
        disableConfirm={disableConfirm}
        lifecycleBusyId={lifecycleBusyId}
        onDisableClose={() =>
          lifecycleBusyId === null ? setDisableConfirm(null) : undefined
        }
        onDisableConfirm={() =>
          disableConfirm && void setDocumentActive(disableConfirm, false)
        }
        deleteConfirm={deleteConfirm}
        deletingDocumentId={deletingDocumentId}
        bulkDeleteBusy={bulkDeleteBusy}
        dontShowDeleteConfirmAgain={dontShowDeleteConfirmAgain}
        onDontShowDeleteConfirmAgainChange={setDontShowDeleteConfirmAgain}
        onDeleteClose={closeDeleteConfirmDialog}
        onDeleteConfirm={() => void confirmDelete()}
      />

    </div>
  );
};
